import React, { useEffect, useState, useRef } from "react";
import axios from "../utils/api";
import logo from "../assets/shoe.png";

const fmtKesh = (n) =>
  `${Number(n || 0).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Kesh`;

const DebtsTrackingReport = () => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const reportRef = useRef();

  useEffect(() => {
    fetchDebts();
  }, []);

  const fetchDebts = async () => {
    try {
      const res = await axios.get(`/debts-tracking.php`);
      if (Array.isArray(res.data)) {
        setData(res.data);
        setError(null);
      } else {
        throw new Error("no debt report data returned");
      }
    } catch (err) {
      console.error("error loading debt report:", err);
      setError("failed to load debt report.");
      setData([]);
    }
  };

  const handlePrint = () => window.print();

  const getAddressBlock = (shop_id) => {
    const id = Number(shop_id);
    switch (id) {
      case 1:
        return (
          <>
            <p>
              <strong>NAIROBI, KENYA</strong>
            </p>
            <p>kamukunji market</p>
            <p>building: marpet plaza, first floor, shop no. 20</p>
            <p>contact: +254740357257/+254745833965</p>
          </>
        );
      case 2:
        return (
          <>
            <p>
              <strong>NAIROBI, KENYA</strong>
            </p>
            <p>kamukunji market</p>
            <p>building: marpet plaza, second floor, shop no. 46</p>
            <p>contact: +2540740357257/+254745833965</p>
          </>
        );
      default:
        return <>location unknown</>;
    }
  };

  const openPaymentModal = (debt) => {
    setSelectedDebt(debt);
    const amount = parseFloat(debt.total_amount ?? debt.amount ?? 0);
    const paid = parseFloat(debt.amount_paid ?? 0);
    setPaymentAmount(Math.max(amount - paid, 0).toFixed(2));
  };

  const submitPayment = async () => {
    if (!paymentAmount || isNaN(paymentAmount) || Number(paymentAmount) <= 0) {
      alert("enter a valid amount.");
      return;
    }
    try {
      await axios.post("/debts-tracking.php", {
        debt_id: selectedDebt.id,
        amount: paymentAmount,
      });
      alert("payment recorded successfully.");
      setSelectedDebt(null);
      setPaymentAmount("");
      fetchDebts();
    } catch (err) {
      console.error("payment failed:", err);
      alert("payment failed.");
    }
  };

  // filters
  const filteredData = data.filter((item) => {
    const matchesSearch = (item.customer_name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const paidAt = item.paid_at?.split(" ")[0] || item.created_at?.split(" ")[0];

    const matchesDate = selectedDate ? paidAt === selectedDate : true;
    const matchesMonth = selectedMonth ? paidAt?.startsWith(selectedMonth) : true;
    const matchesRange =
      startDate && endDate ? paidAt >= startDate && paidAt <= endDate : true;

    return matchesSearch && matchesDate && matchesMonth && matchesRange;
  });

  // column totals over filtered data
  const totals = filteredData.reduce(
    (acc, item) => {
      const amount = parseFloat(item.total_amount ?? item.amount ?? 0);
      const paid = parseFloat(item.amount_paid ?? 0);
      const balance = amount - paid;
      acc.amount += amount;
      acc.paid += paid;
      acc.balance += balance;
      return acc;
    },
    { amount: 0, paid: 0, balance: 0 }
  );

  return (
    <div className="p-4 print:p-0">
      {/* header controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4 print:hidden">
        <h2 className="text-2xl font-bold text-red-700 mr-auto">
          💸 debts tracking report
        </h2>

        <input
          type="text"
          placeholder="search customer..."
          className="px-3 py-1 border rounded text-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <input
          type="date"
          className="px-3 py-1 border rounded text-sm"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <input
          type="month"
          className="px-3 py-1 border rounded text-sm"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />
        <input
          type="date"
          className="px-3 py-1 border rounded text-sm"
          placeholder="start date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <input
          type="date"
          className="px-3 py-1 border rounded text-sm"
          placeholder="end date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <button
          onClick={handlePrint}
          className="bg-red-600 text-white px-4 py-1 rounded hover:bg-red-700 transition"
        >
          🖨️ print report
        </button>
      </div>

      {/* printable report */}
      <div
        ref={reportRef}
        className="bg-white p-6 rounded shadow print:shadow-none print:p-6 print:bg-white print:text-black print:block"
      >
        {/* header (logo + address) */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-start space-x-2">
            <img src={logo} alt="logo" className="h-12 w-12 print:h-12 print:w-12" />
            <div>
              <h2 className="text-base font-bold uppercase print:text-sm">
                petrah collection
              </h2>
              <p className="text-sm text-gray-500 print:text-xs">kamukunji market</p>
            </div>
          </div>
          <div className="text-right text-sm leading-tight print:text-xs">
            {data[0]?.shop_id && getAddressBlock(data[0].shop_id)}
          </div>
        </div>

        {/* title */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold uppercase print:text-lg">
            customers with outstanding debts
          </h3>
          <p className="text-xs text-gray-400">
            generated on {new Date().toLocaleString()}
          </p>
        </div>

        <table className="w-full border-collapse border border-gray-300 text-xs print:text-[11px]">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">#</th>
              <th className="border px-2 py-1">customer</th>
              <th className="border px-2 py-1">phone</th>
              <th className="border px-2 py-1">sale id</th>
              <th className="border px-2 py-1">amount (Kesh)</th>
              <th className="border px-2 py-1">paid (Kesh)</th>
              <th className="border px-2 py-1">balance (Kesh)</th>
              <th className="border px-2 py-1">status</th>
              <th className="border px-2 py-1">last payment</th>
              <th className="border px-2 py-1 print:hidden">action</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-3 text-gray-500">
                  no debts found.
                </td>
              </tr>
            ) : (
              filteredData.map((item, index) => {
                const amount = parseFloat(item.total_amount ?? item.amount ?? 0);
                const paid = parseFloat(item.amount_paid ?? 0);
                const balance = amount - paid;

                return (
                  <tr key={item.id}>
                    <td className="border px-2 py-1 text-center">{index + 1}</td>
                    <td className="border px-2 py-1">{item.customer_name}</td>
                    <td className="border px-2 py-1">{item.phone || "—"}</td>
                    <td className="border px-2 py-1 text-center">{item.sale_id}</td>
                    <td className="border px-2 py-1 text-right">{fmtKesh(amount)}</td>
                    <td className="border px-2 py-1 text-right">{fmtKesh(paid)}</td>
                    <td className="border px-2 py-1 text-right text-red-600 font-semibold">
                      {fmtKesh(balance)}
                    </td>
                    <td className="border px-2 py-1 text-center">
                      {item.status === "paid" ? "✅ paid" : "⏳ unpaid"}
                    </td>
                    <td className="border px-2 py-1 text-center">
                      {item.paid_at ? item.paid_at.split(" ")[0] : "—"}
                    </td>
                    <td className="border px-2 py-1 text-center print:hidden">
                      {item.status === "unpaid" && (
                        <button
                          onClick={() => openPaymentModal(item)}
                          className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                        >
                          pay
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>

          {/* totals row for filtered data */}
          {filteredData.length > 0 && (
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td colSpan="4" className="border px-2 py-2 text-right">
                  totals
                </td>
                <td className="border px-2 py-2 text-right">{fmtKesh(totals.amount)}</td>
                <td className="border px-2 py-2 text-right">{fmtKesh(totals.paid)}</td>
                <td className="border px-2 py-2 text-right text-red-700">
                  {fmtKesh(totals.balance)}
                </td>
                <td className="border px-2 py-2" colSpan="3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* payment modal */}
      {selectedDebt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-2">
              pay debt (sale id: {selectedDebt.sale_id})
            </h3>
            <p className="mb-2">
              customer: <strong>{selectedDebt.customer_name}</strong>
            </p>
            <label className="block text-sm mb-1">enter payment amount (Kesh):</label>
            <input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className="w-full mb-4 px-3 py-2 border rounded"
              min="1"
              step="0.01"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setSelectedDebt(null)}
                className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
              >
                cancel
              </button>
              <button
                onClick={submitPayment}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                pay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtsTrackingReport;
